import { Accordion, AccordionSummary, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem, OutlinedInput, Select, Stack, TextField } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep, debounce, get, isArray, isEqual, min, set } from "lodash";
import { treeActions } from "../../../../redux/slices/treeSlice";
import LightTooltip from "../../../../commonComponents/LightTooltip";
import LabeledDropDown from "../../../TemplateProperties/TPDropDown";
import InboundConditionModal from "./InboundConditionModal";


  //get workflow groups. and on selection of a wfgroup, get templates for that group. view button redirects to that template in inbound page
  // Helper functions for template name handling
  const addJsonExtension = (templateName) => {
    if (!templateName || ['SELECT', 'EMPTY_COMBO'].includes(templateName)) return null;
    return templateName.endsWith('.json') ? templateName : `${templateName}.json`;
  };

  const removeJsonExtension = (templateName) => {
    if (!templateName) return '';
    return templateName.endsWith('.json') ? templateName.slice(0, -5) : templateName;
  };

export default function TDataInboundDependency({ treeData, setTreeData, treeSelectedNode, setTreeSelectedNode }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { account } = useSelector((state) => state.queryParams);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);

  const inboundTemplatesTree = useSelector(
    (state) => state.treeData.inbound.leftTree.tree.body.translatedData
  );


  //save function. clone data, find node in tdata, change obj, dispatch new tdata and selnode
  const submitChange = (key, value, bulk, removeValues) => {
    if (!key && !bulk) return;
    // Clone and deeply iterate through the tree data to update the relevant node.
    const updatedData = eachDeep(cloneDeep(treeData), (obj) => {
      // if (obj?.key !== treeSelectedNode?.key) return;

      if (obj?.key === treeSelectedNode?.key) {
        if (bulk) {
          bulk.forEach((item) => {
            if (item.value === null) {
              if (obj?.hasOwnProperty(item.key)) delete obj[item.key];
            } else {
              if (obj?.hasOwnProperty(item.key)) obj[item.key] = item.value;
              else obj[item.key] = item.value;
            }
          });
        } else {
          if (key === "TEMPLATE_LOCATION") {
            if (obj?.hasOwnProperty("TEMPLATE")) delete obj["TEMPLATE"];
            if (obj?.hasOwnProperty("tval")) delete obj["tval"];
            if (value === null) {
              //setting TEMPLATE_LOCATION to '-- Select -- option doesn't even change the data... 
              // if (obj?.hasOwnProperty(key)) delete obj[key]; 
              if (obj?.hasOwnProperty("tgval")) delete obj["tgval"];
            } else {
              obj[key] = value;
              obj.tgval = value;
            }
          } else {
            // Update properties directly on the object.
            if (value === null) {
              if (obj?.hasOwnProperty(key)) delete obj[key];
              obj.tval = null;
              if (obj.TEMPLATE_LOCATION) obj.tgval = obj.TEMPLATE_LOCATION;
            } else {
              obj[key] = value;
              if (key === "TEMPLATE") {
                obj.tval = value;
                if (obj.TEMPLATE_LOCATION) obj.tgval = obj.TEMPLATE_LOCATION;
              }
            }
          }
        }

        setTreeSelectedNode(obj);
      }

      // If the object has children, return them for further iteration.
      if (isArray(obj?.children)) {
        return obj.children;
      }
    });

    setTreeData(updatedData);
  };

  const debounceChangeHandler = useCallback(debounce(submitChange, 100), [rightTree.tree]);


  //***logic for Template Group dropdown ***/
  const templateGroupFlatArr = inboundTemplatesTree?.filter((node) => node?.type === "workflowGroup").map(obj => obj.title) || [];

  let wfGroups = [
    <MenuItem key={'SELECT'} value={'SELECT'}>
      {t("SELECT")}
    </MenuItem>
  ];

  if (templateGroupFlatArr) {
    templateGroupFlatArr.forEach((ele) => {
      wfGroups.push(
        <MenuItem key={ele} value={ele}>
          {ele}
        </MenuItem>
      );
    });
  }

  const currentTemplateGroup = treeSelectedNode?.TEMPLATE_LOCATION ? treeSelectedNode.TEMPLATE_LOCATION.split("/")[1] : "";

  const templateGroupOptions = wfGroups?.length > 0 ?
    wfGroups
    : [
      <MenuItem key={'EMPTY_COMBO'} value={'EMPTY_COMBO'}>
        {t("EMPTY_COMBO")}
      </MenuItem>
    ];

  const templateGroupDefault = currentTemplateGroup ? currentTemplateGroup : templateGroupOptions[0]?.props?.value;

  const [templateGroupValue, setTemplateGroupValue] = useState(templateGroupDefault);

  const handleTemplateGroupChange = async (e) => {
    if (!e.target?.value) return;

    if (e.target.value !== templateGroupValue) {
      setIbTemplateListLoading(true);
      setTemplateGroupValue(e.target.value);
      submitChange('TEMPLATE_LOCATION', ['SELECT', 'EMPTY_COMBO'].includes(e.target.value) ? null : `${account}/${e.target.value}/inbound_templates`);

      //small timeout to allow rerender that sets new templateOptions arr. 
      setTimeout(() => {
        // Check if selected workflow group has templates
        const newTemplatesForWfGroup = inboundTemplatesTree?.filter((node) => node?.type === "workflowGroup" && node?.title === e.target.value)[0]?.children || [];
        const newActualTemplates = newTemplatesForWfGroup?.filter((e) => e.type === "inboundTemplate") || [];
        
        if (newActualTemplates.length > 0) {
          setTemplateValue("SELECT");
        } else {
          setTemplateValue("EMPTY_COMBO");
        }
        setIbTemplateListLoading(false);
      }, 150);
    }

  };


  //*** logic for Template dropdown */
  const currentSelectedTemplate = treeSelectedNode?.TEMPLATE ? removeJsonExtension(treeSelectedNode.TEMPLATE) : "";
  const templatesForWfGroup = inboundTemplatesTree?.filter((node) => node?.type === "workflowGroup" && node?.title === templateGroupValue)[0]?.children || [];

  // Get actual templates (not including default options)
  const actualTemplates = templatesForWfGroup?.filter((e) => e.type === "inboundTemplate") || [];
  let templateMenuOptionsArr = []

  if (actualTemplates?.length > 0) {
    // If we have actual templates, include "SELECT" option
    templateMenuOptionsArr.push(
      <MenuItem key={'SELECT'} value={'SELECT'}>
        {t("SELECT")}
      </MenuItem>
    );
    
    actualTemplates.forEach((ele) => {
      const templateName = removeJsonExtension(ele.title);
      templateMenuOptionsArr.push(
        <MenuItem key={templateName} value={templateName}>
          {templateName}
        </MenuItem>
      );
    });
  } else {
    // If no templates available, show "EMPTY_COMBO"
    templateMenuOptionsArr.push(
      <MenuItem key={'EMPTY_COMBO'} value={'EMPTY_COMBO'}>
        {t("EMPTY_COMBO")}
      </MenuItem>
    );
  }

  // if (currentSelectedTemplate && !templatesForWfGroup.filter((e) => e.type === "inboundTemplate").includes(currentSelectedTemplate)) {
  //   let itemExists = templateMenuOptionsArr.filter((ele) => ele.key === currentSelectedTemplate).length > 0;
  //   if (!itemExists) {
  //     templateMenuOptionsArr.push(
  //       <MenuItem key={currentSelectedTemplate} value={currentSelectedTemplate}>
  //         {currentSelectedTemplate}
  //       </MenuItem>
  //     )
  //   }
  // }

  const [templateOptions, setTemplateOptions] = useState(templateMenuOptionsArr);


  const defaultTemplateValue = (currentSelectedTemplate && templateMenuOptionsArr.map((ele) => ele.key).includes(currentSelectedTemplate)) ?
    currentSelectedTemplate
    : templateOptions[0]?.props?.value;

  const [templateValue, setTemplateValue] = useState(defaultTemplateValue);
  const [templateWfGroup, setTemplateWfGroup] = useState(treeSelectedNode?.TEMPLATE_LOCATION?.split("/")?.[1]);
  const [templateName, setTemplateName] = useState(removeJsonExtension(treeSelectedNode?.TEMPLATE));
  const [ibTemplateListLoading, setIbTemplateListLoading] = useState(false);
  
  // Track the currently selected template value for button state
  const [selectedTemplateValue, setSelectedTemplateValue] = useState(defaultTemplateValue);



  useEffect(() => {
    setTemplateWfGroup(treeSelectedNode?.TEMPLATE_LOCATION?.split("/")?.[1]);
    setTemplateName(removeJsonExtension(treeSelectedNode?.TEMPLATE));
    
    // Update template group value from the selected node
    const newTemplateGroup = treeSelectedNode?.TEMPLATE_LOCATION?.split("/")?.[1];
    if (newTemplateGroup) {
      setTemplateGroupValue(newTemplateGroup);
    } else {
      // If no template location, set to first available option
      setTemplateGroupValue(templateGroupOptions[0]?.props?.value);
    }
    
    // Update template value from the selected node
    if (treeSelectedNode?.TEMPLATE && treeSelectedNode?.TEMPLATE_LOCATION) {
      const newRawValue = removeJsonExtension(treeSelectedNode.TEMPLATE);
      setSelectedTemplateValue(newRawValue);
      setTemplateValue(newRawValue);
      
      // Get current available template names
      const currentWfGroup = treeSelectedNode?.TEMPLATE_LOCATION?.split("/")?.[1];
      const currentTemplatesForWfGroup = inboundTemplatesTree?.filter((node) => node?.type === "workflowGroup" && node?.title === currentWfGroup)[0]?.children || [];
      const currentAvailableTemplateNames = currentTemplatesForWfGroup?.filter((e) => e.type === "inboundTemplate").map(ele => removeJsonExtension(ele.title)) || [];
      
      // Check if template exists in available templates
      if (currentAvailableTemplateNames.length > 0 && !currentAvailableTemplateNames.includes(newRawValue) && newRawValue !== "EMPTY_COMBO" && newRawValue !== "SELECT") {
        // Template doesn't exist in available list
        if (currentAvailableTemplateNames.length > 0) {
          setSelectedTemplateValue("SELECT");
          setTemplateValue("SELECT");
        } else {
          setSelectedTemplateValue("EMPTY_COMBO");
          setTemplateValue("EMPTY_COMBO");
        }
      }
    } else {
      // No template set, reset to appropriate default
      const currentWfGroup = newTemplateGroup || templateGroupValue;
      const currentTemplatesForWfGroup = inboundTemplatesTree?.filter((node) => node?.type === "workflowGroup" && node?.title === currentWfGroup)[0]?.children || [];
      const currentActualTemplates = currentTemplatesForWfGroup?.filter((e) => e.type === "inboundTemplate") || [];
      
      if (currentActualTemplates.length > 0) {
        setSelectedTemplateValue("SELECT");
        setTemplateValue("SELECT");
      } else {
        setSelectedTemplateValue("EMPTY_COMBO");
        setTemplateValue("EMPTY_COMBO");
      }
    }
  }, [treeSelectedNode, inboundTemplatesTree]);

  //check if template exists in inboundTemplatesTree data
  const templateExistsIbTemplatesTree = inboundTemplatesTree?.filter((node) => node?.type === "workflowGroup" && node?.title === templateWfGroup)[0]?.children?.filter((node) => removeJsonExtension(node?.title) === templateName)?.length > 0;

  // raw value from the json , or empty
  let rawTemplateValue = templateValue;

  if (treeSelectedNode?.TEMPLATE && treeSelectedNode?.TEMPLATE_LOCATION) {
    rawTemplateValue = removeJsonExtension(treeSelectedNode.TEMPLATE);
  }

  // Get available template names for existence checking
  const availableTemplateNames = actualTemplates?.map(ele => removeJsonExtension(ele.title)) || [];
  
  // Check if template exists in the available templates
  const templateExistsInAvailableTemplates = availableTemplateNames.includes(rawTemplateValue);

  // If template doesn't exist in the list and we have other templates available, show "SELECT"
  // If no templates are available at all, show "EMPTY_COMBO"
  // But only if the current template is not already a valid selection
  if (!templateExistsInAvailableTemplates && rawTemplateValue !== "EMPTY_COMBO" && rawTemplateValue !== "SELECT") {
    if (availableTemplateNames.length > 0) { // We have templates available
      rawTemplateValue = "SELECT";
    } else {
      rawTemplateValue = "EMPTY_COMBO";
    }
  }

  const handleTemplateChange = (e) => {
    if (!e.target?.value) return;
    setTemplateValue(e.target.value);
    setSelectedTemplateValue(e.target.value);
    debounceChangeHandler('TEMPLATE', addJsonExtension(e.target.value));
  };

  const handleViewTemplateClick = () => {
    if (templateExistsIbTemplatesTree) {
      dispatch(treeActions.setCurrentScreen("inbound"));
      dispatch(treeActions.setForceTemplate({
        workflowGroup: templateWfGroup,
        template: templateName,
        screen: "inbound"
      }));
    }
  };


  useEffect(() => {
    setTemplateOptions(templateMenuOptionsArr);

    // Update template value if current value is not in the new options
    const currentOptions = templateMenuOptionsArr.map((option) => option?.props?.value);
    if (!currentOptions.includes(templateValue)) {
      setTemplateValue(templateMenuOptionsArr[0]?.props?.value);
    }
  }, [inboundTemplatesTree, templateGroupValue]);

  useEffect(() => {
    if (templateOptions.map(node => node.props.value).includes(removeJsonExtension(treeSelectedNode?.TEMPLATE))) {
      setTemplateValue(removeJsonExtension(treeSelectedNode?.TEMPLATE));
    }

  }, [templateOptions]);

  let correctedWfOptions = [...templateGroupOptions];
  let rawWfValue = treeSelectedNode?.TEMPLATE_LOCATION?.split("/")?.[1] || null;

  if (rawWfValue !== null && !templateGroupOptions.map(node => node.props.value).includes(rawWfValue)) {
    correctedWfOptions.push(
      <MenuItem key={rawWfValue} value={rawWfValue}>
        {rawWfValue}
      </MenuItem>
    )
  }

  let correctedTemplateOptions = [...templateOptions];

  if (rawTemplateValue !== null && !templateOptions.map(node => node.props.value).includes(rawTemplateValue)) {
    correctedTemplateOptions.push(
      <MenuItem key={rawTemplateValue} value={rawTemplateValue}>
        {rawTemplateValue}
      </MenuItem>
    )
  }
  

  return (
    <>
      <Stack spacing={"10px"}>
        <LabeledDropDown
          label={t("LBL_TEMP_GROUP")}
          options={correctedWfOptions}
          value={rawWfValue !== null ? rawWfValue : templateGroupOptions[0]?.props?.value}
          onChange={handleTemplateGroupChange}
        />

        <Stack direction={"row"} spacing={"4px"} className='stack-row'>
          {ibTemplateListLoading ? (
            <LightTooltip
              title={t("LOADING_TEMPLATES")}
              placement="top"
            >
              <TextField
                size="small"
                variant="outlined"
                label={t("LBL_TEMP")}
                sx={{height: "34px", width: "100%"}}
                value={
                  templateExistsInAvailableTemplates ? rawTemplateValue : 
                  actualTemplates.length > 0 ? t("SELECT") : t("EMPTY_COMBO")
                }
                disabled={true}
                InputProps={{
                  sx: {height: "34px", paddingRight: "12px !important"},
                  endAdornment: (
                    <CircularProgress size={"14px"} />
                  ),
                }}
              />
            </LightTooltip>
          ) : (
            <LabeledDropDown
              label={t("LBL_TEMP")}
              options={correctedTemplateOptions}
              value={templateExistsInAvailableTemplates ? rawTemplateValue : 
                     actualTemplates.length > 0 ? "SELECT" : "EMPTY_COMBO"}
              onChange={handleTemplateChange}
            />
          )}
          {(
            !templateExistsInAvailableTemplates &&
            ["EMPTY_COMBO", "SELECT"].includes(selectedTemplateValue)
          ) ? (
            <LightTooltip
              title={t("INBOUND_TEMPLATE_NOT_FOUND")}
              arrow
              placement="right"
            >
              <div style={{ height: "37px" }}>
                <Button
                  size='small'
                  disabled={
                    (["EMPTY_COMBO", "SELECT"].includes(selectedTemplateValue)
                      || ibTemplateListLoading
                    )
                  }
                  variant='outlined'
                  sx={{
                    width: "40px",
                    minWidth: "40px",
                    height: "37px"
                  }}
                  onClick={handleViewTemplateClick}
                >
                  {t("MENU_VIEW")}
                </Button>
              </div>
            </LightTooltip>
          ) : (
            <Button
              size='small'
              disabled={(["EMPTY_COMBO", "SELECT"].includes(selectedTemplateValue) || ibTemplateListLoading)}
              variant='outlined'
              sx={{
                width: "40px",
                minWidth: "40px",
              }}
              onClick={handleViewTemplateClick}
            >
              {t("MENU_VIEW")}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* {(treeSelectedNode && treeSelectedNode.type === "inbound") && (
        <InboundConditionModal node={treeSelectedNode} valuesHandler={handleInboundModalSave} />
      )} */}
    </>
  );
};

const styles = {
  getDataNameAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  treeHeaderContainer: {
    paddingTop: '8px',
    paddingBottom: '2px',
    paddingLeft: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  treeContainer: {
    height: '100%',
    width: '100%',
    border: '1px solid #999',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderRadius: '4px',
    minHeight: "161px",
    maxHeight: "161px",
    overflowY: "auto"
  },
  fpContainer: {
    // padding: "0 16px 16px 16px"
    padding: "0 4px 4px 4px",
  },
  accordianContainer: {
    border: "none",
    padding: "0",
    margin: "0",
    boxShadow: "none",
    backgroundColor: "unset",
  },
  accordianHeader: {
    border: "none",
    padding: "0",
    margin: "0",
    backgroundColor: "unset",
  },
  accordianContent: {
    border: "none",
    padding: "0 0px",
    margin: "0",
    backgroundColor: "unset",
  },
};